import type { PlotlyDataLayoutConfig } from 'plotly.js';
import type { AgentEventType, AgentMessage, AgentTimelineEvent } from '../types';
import { isActive, type Turn } from './contract';
import type { PendingCommand, SessionState } from './runtime';

const activityTypes: Record<string, AgentEventType> = {
  thinking: 'thinking_text_chunk',
  tool: 'tool_finished',
  skill: 'skill_activated',
  knowledge: 'kb_read',
  sandbox: 'sandbox_result',
  context: 'context_usage',
  usage: 'context_usage',
  warning: 'error',
};

export function turnMessages(turn: Turn, feedback: AgentMessage['feedback']): AgentMessage[] {
  const latency =
    turn.state.first_output_at && turn.state.output_started_at
      ? Math.max(
          0,
          Date.parse(turn.state.first_output_at) - Date.parse(turn.state.output_started_at),
        )
      : undefined;

  return turn.entries
    .filter((entry) => entry.kind !== 'user' || !entry.steer || entry.steer.status === 'applied')
    .map((entry): AgentMessage => {
      const base = {
        id: entry.id,
        turnId: turn.turn_id,
        createdAt: entry.created_at,
        firstOutputLatencyMs: latency,
      };

      if (entry.kind === 'user')
        return {
          ...base,
          type: 'user',
          content: entry.text,
          attachments: entry.attachments,
          steer: entry.steer
            ? {
                clientSteerId: entry.steer.client_steer_id ?? entry.steer.steer_id,
                steerId: entry.steer.steer_id,
                status: entry.steer.status,
              }
            : undefined,
        };
      if (entry.kind === 'activity')
        return {
          ...base,
          type: 'activity',
          content: '',
          events: entry.items.map(
            (item): AgentTimelineEvent => ({
              id: item.id,
              type:
                item.status === 'active' && item.kind !== 'thinking'
                  ? 'tool_started'
                  : activityTypes[item.kind],
              label:
                item.detail?.trim() ||
                item.tool_name
                  ?.trim()
                  .replaceAll('_', ' ')
                  .replace(/\b\w/g, (character) => character.toUpperCase()) ||
                item.label,
              detail: item.detail ?? undefined,
              toolName: item.tool_name,
              status: item.status === 'active' ? undefined : item.status,
            }),
          ),
        };

      return {
        ...base,
        type: 'assistant',
        isStreaming: isActive(turn),
        feedback,
        content: entry.blocks
          .filter((block) => block.kind === 'markdown')
          .map((block) => block.text)
          .join(''),
        plots: entry.blocks
          .filter((block) => block.kind === 'plot')
          .map(
            (block) =>
              ({
                data: block.data,
                layout: block.layout,
                config: block.config ?? undefined,
              }) as PlotlyDataLayoutConfig,
          ),
        mapViews: entry.blocks.filter((block) => block.kind === 'map'),
        artifacts: entry.blocks
          .filter((block) => block.kind === 'artifact')
          .map((block) => ({
            id: block.artifact_id,
            kind: block.artifact_kind,
            filename: block.filename,
            byteSize: block.byte_size,
            downloadUrl: block.download_url,
          })),
        resourceReferences: entry.blocks.flatMap((block) =>
          block.kind === 'references' ? block.references : [],
        ),
        appCommands: entry.blocks.flatMap((block) =>
          block.kind === 'app_command'
            ? [
                {
                  command: block.command,
                  originClientId: block.origin_client_id,
                  status: block.status,
                  reason: block.reason,
                },
              ]
            : [],
        ),
      };
    });
}

export function selectConversation(session: SessionState, id: string | undefined) {
  const conversation = id ? session.conversations[id] : undefined;
  const turns = Object.values(conversation?.turns ?? {}).sort(
    (a, b) => a.turn_index - b.turn_index,
  );
  const active = turns.find(isActive);
  const pending = Object.values(session.pending).filter(
    (item) => item.command.conversation_id === id,
  );
  const messages = turns.flatMap((turn) => {
    const rendered = turnMessages(turn, conversation?.feedback[turn.turn_id]);
    const liveActivity = isActive(turn)
      ? rendered.findLast((message) => message.type === 'activity')
      : undefined;

    return rendered.filter((message) => message !== liveActivity && hasVisibleContent(message));
  });

  messages.push(...optimisticMessages(pending, turns));

  const running = Boolean(
    active || pending.some((item) => item.command.type === 'turn' && !item.error),
  );
  const latest = active ?? turns.at(-1);
  const output = latest?.state;

  return {
    messages,
    pending,
    isRunning: running,
    isHydrated: Boolean(conversation?.conversation),
    isReconnecting: running && !session.connected,
    isCancelling: Boolean(
      active?.state.cancel_requested_at ||
        pending.some(
          (item) => (item.command.type === 'cancel' || item.cancelAfterAccept) && !item.error,
        ),
    ),
    currentTurnStatus:
      active?.state.status ?? (running ? ('queued' as const) : latest?.state.status),
    currentTurnStartedAtMs: output?.output_started_at
      ? Date.parse(output.output_started_at)
      : undefined,
    firstOutputLatencyMs:
      output?.first_output_at && output.output_started_at
        ? Date.parse(output.first_output_at) - Date.parse(output.output_started_at)
        : undefined,
    timeline: active
      ? (turnMessages(active, undefined).findLast((message) => message.type === 'activity')
          ?.events ?? [])
      : [],
    error: output?.error?.message,
  };
}

function optimisticMessages(pending: PendingCommand[], turns: Turn[]): AgentMessage[] {
  const messages: AgentMessage[] = [];

  for (const item of pending) {
    if (item.command.type !== 'turn') continue;
    if (
      turns.some(
        (turn) =>
          turn.client_turn_id === item.command.command_id ||
          turn.entries.some(
            (entry) =>
              entry.kind === 'user' && entry.steer?.client_steer_id === item.command.command_id,
          ),
      )
    )
      continue;

    messages.push({
      id: item.command.command_id,
      type: 'user',
      content: item.command.prompt ?? item.command.content ?? '',
      attachments: item.attachments,
    });
  }

  return messages;
}

function hasVisibleContent(message: AgentMessage) {
  if (message.type !== 'assistant') return true;

  return Boolean(
    message.content ||
      message.plots?.length ||
      message.mapViews?.length ||
      message.artifacts?.length ||
      message.resourceReferences?.length ||
      message.appCommands?.length,
  );
}
